import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
} from '@angular/router';
import { Observable, filter, map, take } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { PermissionService } from '../services/permission.service';
import { UserStore } from '../../features/user/store/user.store';

/**
 * Interface for route data used by PermissionGuard
 */
export interface PermissionGuardData {
  /** Single permission or array of permissions required to access the route */
  permissions: string | string[];
  /** If true, requires ALL permissions (AND logic). Default is false (OR logic) */
  requireAll?: boolean;
}

/**
 * Route guard that checks if the user has required permissions before allowing navigation.
 * If permission check fails, redirects to /access-denied page.
 *
 * Usage in routing:
 * ```typescript
 * {
 *   path: 'customers',
 *   canActivate: [PermissionGuard],
 *   data: { permissions: 'GET_CUSTOMERS' }
 * }
 * ```
 *
 * For multiple permissions (OR logic):
 * ```typescript
 * data: { permissions: ['GET_TRANSACTIONS', 'GET_BATCH'] }
 * ```
 *
 * For multiple permissions (AND logic):
 * ```typescript
 * data: { permissions: ['ADMIN_EDIT_USER', 'ADMIN_GET_USER'], requireAll: true }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate {
  private permissionService = inject(PermissionService);
  private userStore = inject(UserStore);
  private router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): boolean | Observable<boolean> {
    const data = route.data as PermissionGuardData;

    if (!data.permissions) return true;

    const evaluate = (): boolean => {
      const allowed = data.requireAll
        ? this.permissionService.hasAllPermissions(
            Array.isArray(data.permissions) ? data.permissions : [data.permissions]
          )
        : this.permissionService.hasPermission(data.permissions);
      if (!allowed) {
        this.router.navigate(['/access-denied']);
      }
      return allowed;
    };

    if (this.canEvaluateNow()) {
      return evaluate();
    }

    // User has GROUP_* roles but the map hasn't arrived yet — wait for it.
    return toObservable(this.userStore.roleGroupsMap).pipe(
      filter(groups => Object.keys(groups).length > 0),
      take(1),
      map(() => evaluate()),
    );
  }

  private canEvaluateNow(): boolean {
    const profile = this.userStore.activeProfile();
    const roles = profile?.roles ?? [];
    const hasGroupRole = roles.some(r => r.startsWith('GROUP_'));
    if (!hasGroupRole) return true;
    return Object.keys(this.userStore.roleGroupsMap()).length > 0;
  }
}
